import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import Nav from "./components/common/Nav";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak, { keycloakInitOptions } from "./Keycloak";
import { TimezoneProvider } from "./context/TimezoneContext";
import PrivateRoute from "./components/common/PrivateRoute";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrganizationPage from "./components/organizations/OrganizationPage";
import SourcesPage from "./components/sources/SourcesPage";
import RoutesPage from "./components/routes/RoutesPage";
import AccountPage from "./components/account/AccountPage";
import { ManageOrganizationDetails } from "./components/organizations/ManageOrganizationDetails";
import DestinationsPage from "./components/destinations/DestinationsPage"
import { ManageRouteDetails } from "./components/routes/ManageRouteDetails";
import { useKeycloakToken, LoginModal } from './components/common/useKeycloak';
import { ManageDestinationDetails } from "./components/destinations/ManageDestinationDetails";
import { ManageAddRouteStepOne } from "./components/routes/ManageAddRouteStepOne";
import { ManageAddRouteStepTwo } from "./components/routes/ManageAddRouteStepTwo";
import { ManageDestinationModal } from "./components/destinations/ManageDestinationModal";
import { HelpProvider } from "./context/HelpContext";
import HelpSidePanel from "./components/common/HelpSidePanel";
import { trackPageView, setAnalyticsUserProperties, getPageTitle } from "./analytics/analytics";
import { RequireContactEmailGate } from "./auth/useRequireContactEmail";
import NotFoundPage from "./components/common/NotFoundPage";
import { resetApiCallLog } from "./api/apiUtils";

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    // Developer-only API-call counter (GUNDI-5464): start a fresh cumulative
    // tally for each view. No-op output unless the debug flag is set.
    resetApiCallLog();
    trackPageView(location.pathname, getPageTitle(location.pathname));
  }, [location.pathname]);
  return null;
}

// A record can be a destination in one Route and the source of another, so the
// section that lists them is "Connections". The old /destinations and
// /destination URLs are in bookmarks and support docs — keep them working.
const LegacyDestinationsRedirect = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={pathname.replace(/^\/(connected-systems|destinations?)/, "/connections") + search} replace />;
};

// Several routes render the same details screen (/routes/:id, its tabs, and
// the flow-map selection deep links), so React Router hands the mounted screen a
// new :id instead of building a fresh one. Its data is fetched when it mounts, so
// without this it would keep showing the previous Route under the new URL
// until a reload. Keying on the id makes a different Route a new screen,
// while moving between tabs of the same Route still keeps its state.
const RouteDetailsRoute = () => {
  const { route_id } = useParams();
  return <ManageRouteDetails key={route_id} />;
};

// Same for the Connection screen, reached both on its own and from inside a
// Route.
const DestinationDetailsRoute = () => {
  const { dest_id } = useParams();
  return <div><ManageDestinationDetails key={dest_id} /></div>;
};

function App() {
  return (
    <div className="min-h-screen bg-very-light-gray">
      <ReactKeycloakProvider authClient={keycloak} initOptions={keycloakInitOptions}>
        <TimezoneProvider>
          <HelpProvider>
            <BrowserRouter>
              <PageViewTracker />
              <RequireContactEmailGate />
              <Nav />
              <HelpSidePanel />
              <div className="px-8 desktop:px-[60px] py-6 pt-[115px]">
                <Routes>
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <RoutesPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/account"
                    element={
                      <PrivateRoute>
                        <AccountPage />
                      </PrivateRoute>
                    } />
                  
                  {/* workspaces */}
                  <Route
                    path="/workspaces"
                    element={
                      <PrivateRoute>
                        <OrganizationPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/workspaces/:org_id"
                    element={
                      <PrivateRoute>
                        <ManageOrganizationDetails />
                      </PrivateRoute>
                    } />
                  
                  {/* sources */}
                  <Route
                    path="/sources" // sources (list)
                    element={
                      <PrivateRoute>
                        <SourcesPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/sources/destination/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  
                  {/* Connections — every integration record, whatever role it
                      plays in a route. /destinations* redirects here. */}
                   <Route
                    path="/connections"
                    element={
                      <PrivateRoute>
                        <DestinationsPage />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/new"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:dest_id"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/connections/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  {/* /connected-systems was this screen's URL on main, so links
                      minted then (tickets, Slack, GA landing pages) still resolve. */}
                  <Route path="/connected-systems" element={<LegacyDestinationsRedirect />} />
                  <Route path="/connected-systems/*" element={<LegacyDestinationsRedirect />} />
                  <Route path="/destinations" element={<LegacyDestinationsRedirect />} />
                  <Route path="/destinations/*" element={<LegacyDestinationsRedirect />} />
                  <Route path="/destination/*" element={<LegacyDestinationsRedirect />} />
                  <Route
                    path="/routes/:route_id"
                    element={
                      <PrivateRoute>
                        <RouteDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage"
                    element={
                      <PrivateRoute>
                        <RouteDetailsRoute />
                      </PrivateRoute>
                    } />
                  {/* Flow-map selection deep links: the segments after /configurations
                      (account/:id[/:section] or flow/:id/:dir[/:section]) are parsed by
                      useSelectionFromUrl — one splat route instead of one route per depth. */}
                  <Route
                    path="/routes/:route_id/configurations/*"
                    element={
                      <PrivateRoute>
                        <RouteDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage/newdestination"
                    element={
                      <PrivateRoute>
                        <ManageDestinationModal onToggleModal={() => window.history.back()} />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage/:dest_id"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/:route_id/:subpage/:dest_id/configuration"
                    element={
                      <PrivateRoute>
                        <DestinationDetailsRoute />
                      </PrivateRoute>
                    } />

                  {/* Add Route flow  */}
                  <Route
                    path="/routes/new"
                    element={
                      <PrivateRoute>
                        <ManageAddRouteStepOne />
                      </PrivateRoute>
                    } />
                  <Route
                    path="/routes/new/configureprovider/:pt_value"
                    element={
                      <PrivateRoute>
                        <ManageAddRouteStepTwo />
                      </PrivateRoute>
                    } />
                  {/* Catch-all 404 */}
                  <Route
                    path="*"
                    element={
                      <PrivateRoute>
                        <NotFoundPage />
                      </PrivateRoute>
                    } />
                </Routes>
                <ToastContainer />
                <Content />
              </div>
              </BrowserRouter>
          </HelpProvider>
        </TimezoneProvider>
      </ReactKeycloakProvider>
    </div>
  );
}

function Content() {
  const [token, showLoginModal, handleLogin, isSessionExpired] = useKeycloakToken();

  useEffect(() => {
    if (token && typeof token === 'string') {
      setAnalyticsUserProperties(token);
    }
  }, [token]);

  return (
    <>
      {(showLoginModal === true) && <LoginModal showLoginModal={showLoginModal} handleLogin={handleLogin} isSessionExpired={isSessionExpired} />}
    </>
  );
}

export default App;
